import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
 Eye,
 CheckCircle,
 XCircle,
 Clock,
 ShoppingBag,
 Lock,
 Globe,
 User,
} from 'lucide-react';
import DataTable from '../../shared/DataTable';
import { useAdminBookings } from '../../../services/adminBookingService';
import { formatDate, formatTime } from '../../../utils/dateUtils';
import { isPrivateBooking } from '../../../utils/bookingSource';

const STATUS_FILTERS = [
 { value: '', label: 'All' },
 { value: 'pending', label: 'Pending' },
 { value: 'confirmed', label: 'Confirmed' },
 { value: 'in_progress', label: 'In Progress' },
 { value: 'completed', label: 'Completed' },
 { value: 'cancelled', label: 'Cancelled' },
];

const getStatusConfig = (status) => {
 const statusConfig = {
 pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Pending' },
 confirmed: { color: 'bg-surface-muted text-ink', icon: CheckCircle, label: 'Confirmed' },
 in_progress: { color: 'bg-surface-muted text-ink', icon: Clock, label: 'In Progress' },
 completed: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
 cancelled: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Cancelled' },
 no_show: { color: 'bg-gray-100 text-gray-800', icon: XCircle, label: 'No Show' },
 };
 return statusConfig[status] || statusConfig.pending;
};

const getClientDisplayName = (booking) => {
 if (isPrivateBooking(booking)) {
 return booking.guest_name || booking.manual_title || 'Private client';
 }
 return booking.client_display_name || booking.client?.name || 'Unknown Client';
};

const formatMoney = (amount) => {
 const n = Number(amount);
 if (!Number.isFinite(n)) return '—';
 return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const OrdersTab = ({ vendorProfile }) => {
 const navigate = useNavigate();
 const vendorProfileId = vendorProfile?.id;
 const [statusFilter, setStatusFilter] = useState('');
 const [sourceFilter, setSourceFilter] = useState('all');

 const apiParams = useMemo(() => {
 const params = {
 vendor_id: vendorProfileId,
 per_page: 100,
 };
 if (statusFilter) params.status = statusFilter;
 if (sourceFilter === 'platform') params.booking_source = 'platform';
 if (sourceFilter === 'private') params.booking_source = 'vendor_manual';
 return params;
 }, [vendorProfileId, statusFilter, sourceFilter]);

 const { data: bookingsResponse, isLoading, error } = useAdminBookings(apiParams, {
 enabled: !!vendorProfileId,
 });

 const orders = bookingsResponse?.data?.data || [];
 const statistics = bookingsResponse?.statistics || {};
 const totalCount =
 statistics.total_bookings ??
 bookingsResponse?.data?.total ??
 vendorProfile?.bookings_count ??
 orders.length;

 const columns = useMemo(
 () => [
 {
 key: 'booking_reference',
 label: 'Order #',
 sortable: true,
 mobilePrimary: true,
 exportValue: (booking) => booking.booking_reference || '',
 render: (booking) => (
 <div className="min-w-0">
 <div className="flex items-center gap-1.5">
 <span className="font-mono text-sm font-medium text-ink truncate">
 #{booking.booking_reference || booking.id}
 </span>
 {isPrivateBooking(booking) && (
 <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase bg-surface-muted text-ink">
 <Lock className="w-2.5 h-2.5" />
 Private
 </span>
 )}
 </div>
 <p className="text-xs text-gray-500 mt-0.5 truncate">
 {booking.custom_service_name ||
 booking.vendor_service?.service_name ||
 booking.vendor_service_package?.package_name ||
 'Service'}
 </p>
 </div>
 ),
 },
 {
 key: 'client',
 label: 'Client',
 sortable: true,
 exportValue: (booking) => getClientDisplayName(booking),
 render: (booking) => {
 const name = getClientDisplayName(booking);
 const sub = isPrivateBooking(booking)
 ? booking.guest_email || booking.guest_phone || 'Private booking'
 : booking.client?.email || '—';
 return (
 <div className="flex items-center gap-2 min-w-0">
 <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0">
 <User className="w-3.5 h-3.5 text-gray-500" />
 </div>
 <div className="min-w-0">
 <p className="text-sm font-medium text-gray-900 truncate">{name}</p>
 <p className="text-xs text-gray-500 truncate">{sub}</p>
 </div>
 </div>
 );
 },
 },
 {
 key: 'event_date',
 label: 'Event Date',
 sortable: true,
 exportValue: (booking) =>
 booking.event_date
 ? `${formatDate(booking.event_date)}${booking.event_time ? ` ${formatTime(booking.event_time)}` : ''}`
 : '',
 render: (booking) => (
 <div className="text-sm text-gray-900">
 <div>{booking.event_date ? formatDate(booking.event_date) : 'N/A'}</div>
 {booking.event_time && (
 <div className="text-xs text-gray-500">{formatTime(booking.event_time)}</div>
 )}
 </div>
 ),
 },
 {
 key: 'status',
 label: 'Status',
 sortable: true,
 exportValue: (booking) => booking.status || '',
 render: (booking) => {
 const config = getStatusConfig(booking.status);
 const Icon = config.icon;
 return (
 <span
 className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${config.color}`}
 >
 <Icon className="w-3 h-3 mr-1" />
 {config.label}
 </span>
 );
 },
 },
 {
 key: 'total_amount',
 label: 'Amount',
 sortable: true,
 exportValue: (booking) =>
 Number(booking.total_amount ?? booking.amount_paid ?? 0).toFixed(2),
 render: (booking) => (
 <span className="text-sm font-medium text-gray-900">
 {formatMoney(booking.total_amount ?? booking.amount_paid)}
 </span>
 ),
 },
 {
 key: 'created_at',
 label: 'Placed',
 sortable: true,
 exportValue: (booking) =>
 booking.created_at ? formatDate(booking.created_at) : '',
 render: (booking) => (
 <span className="text-sm text-gray-600">
 {booking.created_at ? formatDate(booking.created_at) : 'N/A'}
 </span>
 ),
 },
 ],
 []
 );

 const actions = [
 {
 label: 'View',
 icon: Eye,
 onClick: (booking) => navigate(`/admin/bookings/${booking.id}`),
 title: 'View order details',
 },
 ];

 if (!vendorProfileId) {
 return (
 <div className="text-center py-10 text-gray-500">
 <ShoppingBag className="w-10 h-10 mx-auto mb-3 text-gray-300" />
 <p>Vendor profile not found — orders cannot be loaded.</p>
 </div>
 );
 }

 if (error) {
 return (
 <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
 Failed to load orders: {error?.response?.data?.message || error.message}
 </div>
 );
 }

 return (
 <div className="space-y-4">
 <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
 <div>
 <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
 <ShoppingBag className="w-5 h-5 text-gray-500" />
 Orders
 <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-surface-muted text-ink">
 {totalCount}
 </span>
 </h3>
 <p className="text-sm text-gray-500 mt-1">
 Bookings placed with this vendor
 </p>
 </div>

 <div className="flex flex-wrap gap-2">
 {[
 { key: 'all', label: 'All sources', icon: ShoppingBag },
 { key: 'platform', label: 'Platform', icon: Globe },
 { key: 'private', label: 'Private', icon: Lock },
 ].map(({ key, label, icon: Icon }) => (
 <button
 key={key}
 type="button"
 onClick={() => setSourceFilter(key)}
 className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${ sourceFilter === key ? 'bg-gray-900 text-white border-gray-900' : 'bg-surface text-gray-600 border-gray-300 hover:bg-surface-muted' }`}
 >
 <Icon className="w-3 h-3 mr-1.5" />
 {label}
 </button>
 ))}
 </div>
 </div>

 <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
 {[
 { key: 'total', label: 'Total', value: statistics.total_bookings ?? totalCount },
 { key: 'pending', label: 'Pending', value: statistics.pending_bookings ?? 0 },
 { key: 'confirmed', label: 'Confirmed', value: statistics.confirmed_bookings ?? 0 },
 { key: 'in_progress', label: 'In progress', value: statistics.in_progress_bookings ?? 0 },
 { key: 'completed', label: 'Completed', value: statistics.completed_bookings ?? 0 },
 { key: 'cancelled', label: 'Cancelled', value: statistics.cancelled_bookings ?? 0 },
 ].map((stat) => (
 <div
 key={stat.key}
 className="rounded-lg border border-gray-200 bg-surface-muted px-3 py-2"
 >
 <p className="text-[11px] uppercase tracking-wide text-gray-500">{stat.label}</p>
 <p className="text-lg font-semibold text-gray-900">{stat.value}</p>
 </div>
 ))}
 </div>

 <div className="flex flex-wrap gap-2">
 {STATUS_FILTERS.map((filter) => (
 <button
 key={filter.value || 'all'}
 type="button"
 onClick={() => setStatusFilter(filter.value)}
 className={`px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${ statusFilter === filter.value ? 'bg-surface-muted text-ink border-border' : 'bg-surface text-gray-600 border-gray-200 hover:bg-surface-muted' }`}
 >
 {filter.label}
 </button>
 ))}
 </div>

 <DataTable
 data={orders}
 columns={columns}
 actions={actions}
 loading={isLoading}
 searchable={true}
 filterable={false}
 sortable={true}
 exportable={true}
 exportFileName={`vendor-${vendorProfileId}-orders`}
 pageSize={20}
 showPagination={true}
 searchPlaceholder="Search orders by reference, client, or service…"
 emptyMessage="No orders found for this vendor."
 onRowClick={(booking) => navigate(`/admin/bookings/${booking.id}`)}
 onSearch={(data, term) => {
 const q = term.toLowerCase().trim();
 if (!q) return data;
 return data.filter((booking) => {
 const haystack = [
 booking.booking_reference,
 getClientDisplayName(booking),
 booking.client?.email,
 booking.guest_email,
 booking.guest_phone,
 booking.custom_service_name,
 booking.vendor_service?.service_name,
 booking.status,
 ]
 .filter(Boolean)
 .join(' ')
 .toLowerCase();
 return haystack.includes(q);
 });
 }}
 />
 </div>
 );
};

export default OrdersTab;
